import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const WhatsAppFloat = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const waLink = (msg) => `https://wa.me/${t.common.whatsappNumber}?text=${encodeURIComponent(msg)}`;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="whatsapp-quick-menu"
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="w-[calc(100vw-3rem)] max-w-xs overflow-hidden border border-line bg-surface shadow-[0_20px_60px_rgba(0,0,0,0.55)]"
          >
            <div className="flex items-center gap-3 bg-[#075E54] px-4 py-3.5">
              <WhatsAppIcon className="h-6 w-6 shrink-0 text-white" />
              <div className="min-w-0">
                <p className="truncate font-display text-base font-bold uppercase tracking-wide text-white">
                  {t.whatsapp.menuTitle}
                </p>
                <p className="truncate text-[11px] text-white/75">{t.whatsapp.menuSubtitle}</p>
              </div>
            </div>
            <div className="max-h-[50vh] overflow-y-auto py-1.5">
              {t.whatsapp.options.map((opt) => (
                <a
                  key={opt.key}
                  href={waLink(opt.message)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`whatsapp-option-${opt.key}`}
                  onClick={() => setOpen(false)}
                  className="group flex items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-panel"
                >
                  <span className="text-sm text-cream transition-colors group-hover:text-brand">{opt.label}</span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-ash transition-all group-hover:translate-x-0.5 group-hover:text-brand" />
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setOpen(!open)}
        aria-label={t.whatsapp.aria}
        aria-expanded={open}
        data-testid="whatsapp-floating-button"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, type: "spring", stiffness: 260, damping: 18 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] shadow-[0_8px_30px_rgba(37,211,102,0.35)]"
      >
        {open ? <X className="h-6 w-6 text-white" /> : <WhatsAppIcon className="h-7 w-7 text-white" />}
        {!open && (
          <span className="absolute -top-1 -right-1 flex h-4 w-4">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#25D366] opacity-60" />
            <span className="relative inline-flex h-4 w-4 rounded-full border-2 border-ink bg-[#25D366]" />
          </span>
        )}
      </motion.button>
    </div>
  );
};

export default WhatsAppFloat;
